import React, { PropsWithChildren } from 'react';
import { Box, Typography, Tooltip, Stack } from '@mui/material';
import { CallMade } from '@mui/icons-material';
import { useCRMTagsContext } from '../CRMTagsContext';
import { Tag } from '../types';
import { tagColorCssVar } from './CRMStyles';

// ============================================================================
// 1. BARE CHIP (Pure UI Component)
// ============================================================================

export interface TagChipBareProps {
  tag: Tag;
  onClick?: (e: React.MouseEvent, tag: Tag) => void;
  action?: (e: React.MouseEvent, tag: Tag) => void;
  actionIcon?: React.ReactNode;
}

export const TagChipBare = ({ tag, onClick, action, actionIcon }: TagChipBareProps) => {
  const isClickable = !!onClick;
  
  // Provide a safe fallback color if none is set
  const baseColor = tag.color || "fallback"; 

  const content = (
    <Box
      onClick={(e) => onClick && onClick(e, tag)}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 24,
        px: 1,
        borderRadius: 2, 
        bgcolor: `var(${tagColorCssVar(baseColor)})`,
        border: '1px solid',
        borderColor: 'rgba(0,0,0,0.1)',
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'opacity 0.2s, filter 0.2s',
        userSelect: 'none',
        maxWidth: '100%', // <-- Allows the chip to shrink within its container
        boxSizing: 'border-box',
        '&:hover': isClickable ? {
          filter: 'brightness(0.95)',
        } : {},
      }}
    >
      <Typography 
        variant="caption" 
        sx={{ 
          color: 'rgba(0,0,0,0.87)', 
          fontWeight: 500,
          lineHeight: 1,
          whiteSpace: 'nowrap',
          overflow: 'hidden',        // <-- Hide overflow text
          textOverflow: 'ellipsis',  // <-- Add the "..."
        }}
      >
        {tag.name}
      </Typography>

      {/* Optional action icon at the tail */}
      {action && (
        <Box
          component="span"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation(); 
            action(e, tag);
          }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0, // <-- Prevents the icon from squeezing when text truncates
            ml: 0.5,
            mr: -0.5, 
            p: 0.25,
            borderRadius: '50%',
            cursor: 'pointer',
            color: 'rgba(0,0,0,0.54)',
            '&:hover': {
              bgcolor: 'rgba(0,0,0,0.08)',
              color: 'rgba(0,0,0,0.87)'
            }
          }}
        >
          {actionIcon || <CallMade sx={{ fontSize: 14 }} />}
        </Box>
      )}
    </Box>
  );

  // Always render Tooltip so truncated tags can be read fully
  const tooltipContent = (
    <React.Fragment>
      <Typography variant="subtitle2">{tag.name}</Typography>
      {tag.description && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.9 }}>
          {tag.description}
        </Typography>
      )}
    </React.Fragment>
  );

  return (
    <Tooltip title={tooltipContent} arrow placement="top">
      {content}
    </Tooltip>
  );
};

// ============================================================================
// 2. CONTEXT-AWARE CHIP (Accepts Tag ID)
// ============================================================================

export interface TagChipProps extends Omit<TagChipBareProps, 'tag'> {
  tagId: string | number;
}

export const TagChip = ({ tagId, ...rest }: TagChipProps) => {
  const { tagsMap } = useCRMTagsContext();
  const tag = tagsMap[tagId.toString()];

  if (!tag) {
    return null; 
  }

  return <TagChipBare tag={tag} {...rest} />;
};

// ============================================================================
// 3. TAGS LIST (Renders multiple tag IDs)
// ============================================================================

export interface TagsProps {
  tagIds?: (string | number)[];
  onTagClick?: (e: React.MouseEvent, tag: Tag) => void;
}

export const TagsWrapper = (props: PropsWithChildren<{}>) => {
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5}>
      {props.children}
    </Stack>
  )
}

export const Tags = ({ tagIds = [], onTagClick }: TagsProps) => {
  if (!tagIds || tagIds.length === 0) return null;

  return (
    <TagsWrapper>
      {tagIds.map((id) => (
        <TagChip 
          key={id} 
          tagId={id} 
          onClick={onTagClick} 
        />
      ))}
    </TagsWrapper>
  );
};